import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { api } from '../../utils/apiClient';
import './LeadsPanel.css';

interface Lead {
  id: string;
  type: string;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  message: string | null;
  createdAt: string;
}

function LeadsPanel() {
  const { t } = useTranslation();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<Lead[]>('/api/developer/leads');
      setLeads(data);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(msg || t('leads.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleString();
    } catch {
      return d;
    }
  };

  return (
    <div className="leads-panel">
      <div className="panel-header">
        <h2>{t('leads.title')}</h2>
        <motion.button
          onClick={fetchLeads}
          className="btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
        >{t('common.refresh')}</motion.button>
      </div>

      {error && <div className="panel-error">{error.startsWith('leads.') ? t(error) : error}</div>}

      {loading ? (
        <div className="panel-loading">{t('leads.loading')}</div>
      ) : leads.length === 0 ? (
        <div className="panel-loading">{t('leads.noLeads')}</div>
      ) : (
        <div className="panel-table-wrap">
          <table className="panel-table">
            <thead>
              <tr>
                <th>{t('leads.date')}</th>
                <th>{t('leads.type')}</th>
                <th>{t('common.name')}</th>
                <th>{t('common.email')}</th>
                <th>{t('leads.company')}</th>
                <th>{t('leads.phone')}</th>
                <th>{t('leads.message')}</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td>{formatDate(lead.createdAt)}</td>
                  <td>
                    <span className={`lead-type lead-type-${lead.type}`}>{lead.type}</span>
                  </td>
                  <td>{lead.name}</td>
                  <td>
                    <a href={`mailto:${lead.email}`}>{lead.email}</a>
                  </td>
                  <td>{lead.company || '—'}</td>
                  <td>{lead.phone || '—'}</td>
                  <td className="lead-message">{lead.message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LeadsPanel;
